import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { SortDirection, URLPaths } from 'models/enums';
import { ACCESS_CENTRE_LIST_PAGE_SIZE, ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS } from 'domains/accessCentre/utils';
import Button from 'domains/core/components/Button';
import PageLayout from 'domains/core/components/PageLayout';
import SearchField from 'domains/core/components/SearchField';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import TableOffsetPagination from 'domains/core/components/TableOffsetPagination';
import TableEmptyState from 'domains/core/components/Table/TableEmptyState';
import useDebounce from 'hooks/useDebounce';
import useGetRolesList from 'hooks/queries/useGetRolesList';
import { SEARCH_DEBOUNCE_MS } from 'utils';
import RolesTable from './RolesTable';

const RolesPage = () => {
    const { canManageAccessManagement } = useAccessCentrePermissions();
    const history = useHistory();
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(ACCESS_CENTRE_LIST_PAGE_SIZE);
    const [sortColumnState, setSortColumnState] = useState('last_modified_timestamp');
    const [sortDirectionState, setSortDirectionState] = useState(SortDirection.DESCENDING);

    const offset = (page - 1) * rowsPerPage;

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        setPage(1);
    }, [rowsPerPage]);

    const { data, isLoading, isFetching, isSuccess, refetch } = useGetRolesList({
        limit: rowsPerPage,
        offset,
        searchTerm: debouncedSearch.trim() || undefined,
        sortColumn: sortColumnState,
        sortDirection: sortDirectionState,
        reactQueryOptions: {
            keepPreviousData: true,
        },
    });

    const onSort = (sortColumn: string, direction: SortDirection) => {
        setSortColumnState(sortColumn);
        setSortDirectionState(direction);
        setPage(1);
    };

    const roles = data?.data ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
    const isInitialLoading = isLoading && data === undefined;

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const previousPage = () => {
        if (page > 1) {
            setPage((p) => p - 1);
        }
    };

    const nextPage = () => {
        if (page < totalPages) {
            setPage((p) => p + 1);
        }
    };

    const emptySearch = isSuccess && roles.length === 0 && debouncedSearch.trim().length > 0;

    const headerOthers = (
        <Stack direction="row" marginLeft={8} alignItems="center">
            <SearchField
                placeholder="Search roles..."
                value={searchTerm}
                onChange={setSearchTerm}
                dataTestId="access-center-roles-search-input"
            />
        </Stack>
    );

    const handleCreateRole = () => {
        history.push(`${URLPaths.SETTINGS_ROLES}?id=default`);
    };

    const headerAction = canManageAccessManagement ? (
        <Button variant="contained" onClick={handleCreateRole} data-testid="access-center-roles-create-role-button">
            Create
        </Button>
    ) : undefined;

    return (
        <Stack data-testid="access-center-roles-page" role="main" aria-label="Users and roles - Roles">
            <PageLayout
                header="roles"
                headerOthers={headerOthers}
                {...(headerAction ? { headerAction } : {})}
                showSettingsHeader={true}
            >
                <>
                    <RolesTable
                        roles={roles}
                        isLoading={isLoading}
                        showRoleRowActions={canManageAccessManagement}
                        isRefetching={isFetching && !isInitialLoading}
                        refetch={refetch}
                        onSort={onSort}
                    />
                    {emptySearch && (
                        <Box data-testid="access-center-roles-empty-search">
                            <TableEmptyState searchTerm={debouncedSearch.trim()} />
                        </Box>
                    )}
                    {!isInitialLoading && !emptySearch && (
                        <TableOffsetPagination
                            page={page}
                            totalPages={totalPages}
                            onPreviousPage={previousPage}
                            onNextPage={nextPage}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS}
                            onRowsPerPageChange={setRowsPerPage}
                            testIdPrefix="access-center-roles-pagination"
                        />
                    )}
                </>
            </PageLayout>
        </Stack>
    );
};

export default RolesPage;
